import React from "react";
import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp, Square, ZoomIn, ZoomOut } from "lucide-react";
import { t } from "@client/lib/dictionary";
import { cn } from "@client/lib/cn";
import type { PtzHoldApi } from "@client/hooks/usePtzHold";
import type { PtzAxis } from "@client/lib/foscam/ptzHold";
import HoldRing from "./HoldRing";

/**
 * Směrový kříž + zoom pro PTZ kameru.
 *
 * Tlačítka jsou `<button>` s pointer-capture handlery z `usePtzHold` — držení =
 * pohyb, puštění = stop. Prstenec (`HoldRing`) sedí na tom tlačítku, které se právě
 * drží, takže indikace nepotřebuje žádné další místo nad obrazem.
 *
 * **Jen 4 směry.** Diagonály (`TopLeft`/`TopRight`/`BottomLeft`/`BottomRight`) tahle
 * kamera neumí, takže by to byla čtyři tlačítka, která jen vrátí chybu. Serverový
 * enum `PTZ_DIRECTIONS` zůstává osmisměrný schválně — je to obecný CGI passthrough
 * a jiný model diagonály umět může; omezení je vlastnost téhle kamery, ne API.
 */

interface Props {
  caps: FoscamCaps;
  hold: PtzHoldApi;
  /**
   * `stack` = dlaždice žije ve svislém snap-scrolleru (fullscreen wrapperu). Tlačítka
   * tam musí pustit svislé tažení do scrollu, jinak by se seznam kamer nedal posunout
   * prstem přes ovládání, které je nově trvale viditelné přes celou spodní hranu.
   */
  scrollAxis: "stack" | "none";
  onPoke?: () => void;
}

const DIRS = {
  up: { axis: "Up" as PtzAxis, Icon: ArrowUp, label: "Nahoru" },
  left: { axis: "Left" as PtzAxis, Icon: ArrowLeft, label: "Vlevo" },
  right: { axis: "Right" as PtzAxis, Icon: ArrowRight, label: "Vpravo" },
  down: { axis: "Down" as PtzAxis, Icon: ArrowDown, label: "Dolů" },
};

export const PtzPad: React.FC<Props> = ({ caps, hold, scrollAxis, onPoke }) => {
  const btn = cn(
    "relative inline-flex h-11 w-11 items-center justify-center rounded-md text-white",
    "bg-black/55 hover:bg-black/75 active:bg-black/85 backdrop-blur-sm",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring select-none",
    // `pan-y` ve scrolleru: svislé tažení převezme prohlížeč (scroll) a pošle nám
    // pointercancel → `usePtzHold` pohyb ani neodešle (viz intent delay u dotyku).
    scrollAxis === "stack" ? "touch-pan-y" : "touch-none"
  );

  const activeAxis = hold.state.axis;
  const ringFor = (axis: PtzAxis) => (activeAxis === axis ? hold.tone : "idle");

  const arrow = ({ axis, Icon, label }: { axis: PtzAxis; Icon: React.ElementType; label: string }) => (
    <button
      key={axis}
      type="button"
      className={btn}
      aria-label={`${t.foscam.move}: ${label}`}
      title={`${label} — ${t.foscam.holdHint.toLowerCase()}`}
      onPointerEnter={onPoke}
      {...hold.holdProps(axis)}
    >
      <Icon className="h-5 w-5" />
      <HoldRing progress={hold.progress} tone={ringFor(axis)} />
    </button>
  );

  const stopBtn = (
    <button
      type="button"
      className={cn(btn, hold.state.stopLost && "bg-destructive/80 hover:bg-destructive animate-pulse")}
      aria-label={t.foscam.stop}
      title={t.foscam.stop}
      onClick={() => {
        onPoke?.();
        hold.stopNow();
      }}
    >
      <Square className="h-4 w-4" />
    </button>
  );

  return (
    <div className="flex items-end gap-3">
      {caps.hasPtz && (
        // Plus místo 3×3 mřížky — rohy zůstávají prázdné, aby kříž držel zarovnání.
        <div className="grid w-fit grid-cols-3 gap-1">
          <span />
          {arrow(DIRS.up)}
          <span />
          {arrow(DIRS.left)}
          {stopBtn}
          {arrow(DIRS.right)}
          <span />
          {arrow(DIRS.down)}
          <span />
        </div>
      )}

      {/* Fixní kamera nemá kříž, ale Stop se hodí i tam (zoom se taky zastavuje). */}
      {!caps.hasPtz && caps.hasZoom && stopBtn}

      {caps.hasZoom && (
        <div className="flex flex-col gap-1">
          <button
            type="button"
            className={btn}
            aria-label={t.foscam.zoomIn}
            title={t.foscam.zoomIn}
            onPointerEnter={onPoke}
            {...hold.holdProps("zoomIn")}
          >
            <ZoomIn className="h-5 w-5" />
            <HoldRing progress={hold.progress} tone={ringFor("zoomIn")} />
          </button>
          <button
            type="button"
            className={btn}
            aria-label={t.foscam.zoomOut}
            title={t.foscam.zoomOut}
            onPointerEnter={onPoke}
            {...hold.holdProps("zoomOut")}
          >
            <ZoomOut className="h-5 w-5" />
            <HoldRing progress={hold.progress} tone={ringFor("zoomOut")} />
          </button>
        </div>
      )}
    </div>
  );
};

export default PtzPad;
